import { useMutation } from '@tanstack/react-query'
import { App, Button, Flex, Form, Input, Result, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { useNavigate, useSearchParams } from 'react-router-dom'

import {
  authorizationV2PasswordResetCreateMutation,
  type AuthorizationV2PasswordResetCreateData
} from 'src/api/main'
import { paths } from 'src/router'
import { useInlineApiErrors } from 'src/utils'

import { AuthHeader, AuthLink } from '../components'

const { Text } = Typography

type ResetPasswordConfirmFormValues = Omit<
  AuthorizationV2PasswordResetCreateData['body'],
  'token' | 'service_token'
>

export const ResetPasswordConfirm = () => {
  const [form] = Form.useForm<ResetPasswordConfirmFormValues>()
  const { message } = App.useApp()
  const { showApiErrors, errorAlert } = useInlineApiErrors()

  const [params] = useSearchParams()
  const token = params.get('token') ?? ''

  const navigate = useNavigate()

  const { t } = useTranslation()

  const { mutateAsync: resetPassword, isPending } = useMutation({
    ...authorizationV2PasswordResetCreateMutation(),
    onSuccess: () => {
      message.success(t('password_reset_success'))
      navigate(paths.auth.login)
    },
    onError: (e) => showApiErrors(e, form)
  })

  const onSubmit = async () => {
    const values = await form.validateFields()
    await resetPassword({ body: { token, ...values } })
  }

  if (!token) {
    return (
      <Result
        status="warning"
        title={t('reset_password_missing_token_title')}
        subTitle={t('reset_password_missing_token_description')}
        extra={
          <Button
            type="primary"
            onClick={() => navigate(paths.auth.resetPassword)}
          >
            {t('reset_password_request_new_link')}
          </Button>
        }
      />
    )
  }

  return (
    <Flex orientation="vertical" gap={16} className="w-full">
      <AuthHeader
        title={t('reset_password')}
        subtitle={t('enter_new_password')}
      />

      {errorAlert}

      <Form
        form={form}
        layout="vertical"
        scrollToFirstError
        requiredMark={false}
        id="reset-password-confirm-form"
      >
        <Flex orientation="vertical" gap={16}>
          <Flex orientation="vertical" gap={10}>
            <Form.Item
              name="password"
              label={t('password')}
              style={{ marginBottom: 0 }}
              rules={[
                { required: true },
                {
                  pattern: /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()_+]).{10,}$/,
                  message: t('password_policy')
                }
              ]}
            >
              <Input.Password
                placeholder={t('input_placeholder_password')}
                autoComplete="new-password"
                autoFocus
              />
            </Form.Item>

            <Form.Item
              name="confirm_password"
              label={t('password_repeat')}
              dependencies={['password']}
              hasFeedback
              style={{ marginBottom: 0 }}
              rules={[
                { required: true },
                { min: 8, message: t('password_min_length') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve()
                    }
                    return Promise.reject(new Error(t('passwords_must_match')))
                  }
                })
              ]}
            >
              <Input.Password
                placeholder={t('input_placeholder_password')}
                autoComplete="new-password"
              />
            </Form.Item>
          </Flex>

          <Button
            block
            size="large"
            onClick={onSubmit}
            type="primary"
            loading={isPending}
            form="reset-password-confirm-form"
            htmlType="submit"
          >
            {t('submit')}
          </Button>
        </Flex>
      </Form>

      <Flex align="center" justify="center" gap={6} wrap="wrap">
        <Text type="secondary">{t('have_account')}</Text>
        <AuthLink to={paths.auth.loginPassword}>{t('sign_in')}</AuthLink>
      </Flex>
    </Flex>
  )
}
